import { useState } from "react";
import { motion } from "framer-motion";
import { User, Plus, X } from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { DoulaListing } from "./types";

interface ProfilePreferencesCardProps {
	listing: DoulaListing | null;
	onListingChange: (field: keyof DoulaListing, value: unknown) => void;
}

export default function ProfilePreferencesCard({
	listing,
	onListingChange,
}: ProfilePreferencesCardProps) {
	const [newService, setNewService] = useState("");

	const handleAddService = () => {
		if (!newService.trim() || !listing) return;

		const currentServices = listing.services_offered || [];
		if (currentServices.includes(newService.trim())) {
			alert("Service already exists");
			return;
		}

		const updatedServices = [...currentServices, newService.trim()];
		onListingChange("services_offered", updatedServices);
		setNewService("");
	};

	const handleRemoveService = (serviceToRemove: string) => {
		if (!listing) return;

		const updatedServices =
			listing.services_offered?.filter(
				(service) => service !== serviceToRemove,
			) || [];
		onListingChange("services_offered", updatedServices);
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ delay: 0.25 }}
			className="mt-8"
		>
			<Card className="glass-card border-0 shadow-lg">
				<CardHeader>
					<CardTitle className="text-xl font-karla flex items-center">
						<User className="h-5 w-5 mr-2 text-newTheme-orange" />
						Profile Preferences
					</CardTitle>
					<CardDescription className="font-karla text-newTheme-grey">
						Help families find the right match by sharing your background and
						preferences
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Row 1: Services Offered & Additional Specialties */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Services Offered */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Services Offered
							</Label>
							<div className="mt-2 space-y-3">
								{/* Current Services */}
								<div className="flex flex-wrap gap-2">
									{listing?.services_offered?.length ? (
										listing.services_offered.map((service, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-primary-100 text-primary-800 px-3 py-1 flex items-center gap-2"
											>
												{service}
												<button
													onClick={() => handleRemoveService(service)}
													className="hover:text-red-600 transition-colors"
													type="button"
												>
													<X className="h-3 w-3" />
												</button>
											</Badge>
										))
									) : (
										<p className="text-gray-500 text-sm font-karla">
											No services added yet
										</p>
									)}
								</div>

								{/* Add New Service */}
								<div className="flex gap-2">
									<Input
										placeholder="e.g., Birth Support, Lactation Consulting..."
										value={newService}
										onChange={(e) => setNewService(e.target.value)}
										onKeyUp={(e) => e.key === "Enter" && handleAddService()}
										className="flex-1"
									/>
									<Button
										onClick={handleAddService}
										disabled={!newService.trim()}
										className="bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
										type="button"
									>
										<Plus className="h-4 w-4 mr-1" />
										Add
									</Button>
								</div>
							</div>
						</div>

						{/* Additional Specialties */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Additional Specialties
							</Label>
							<div className="mt-2 space-y-3">
								<div className="flex flex-wrap gap-2">
									{listing?.additional_specialties?.length ? (
										listing.additional_specialties.map((specialty, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-green-100 text-green-800 px-3 py-1 flex items-center gap-2"
											>
												{specialty.length > 30
													? specialty.substring(0, 30) + "..."
													: specialty}
												<button
													onClick={() => {
														const updatedSpecialties =
															listing.additional_specialties?.filter(
																(_, i) => i !== index,
															) || [];
														onListingChange(
															"additional_specialties",
															updatedSpecialties,
														);
													}}
													className="hover:text-red-600 transition-colors"
													type="button"
												>
													X
												</button>
											</Badge>
										))
									) : (
										<p className="text-newTheme-grey text-sm font-karla">
											No additional specialties added yet
										</p>
									)}
								</div>

								<Select
									onValueChange={(value) => {
										if (
											value &&
											!listing?.additional_specialties?.includes(value)
										) {
											const updatedSpecialties = [
												...(listing?.additional_specialties || []),
												value,
											];
											onListingChange(
												"additional_specialties",
												updatedSpecialties,
											);
										}
									}}
								>
									<SelectTrigger className="!bg-white !bg-opacity-100 backdrop-blur-none border border-black shadow-sm">
										<SelectValue placeholder="Add additional specialties..." />
									</SelectTrigger>
									<SelectContent className="bg-white border border-gray-200 shadow-lg">
										{[
											"International Board Certified Lactation Consultant (IBCLC - 1000+ hour certification)",
											"Lactation Specialist/Counselor (50+ hour Certification)",
											"Massage/Bodyworker",
											"Yoga Teacher",
											"Belly binding",
											"Rebozo",
											"Spinning Babies",
											"Hypnobirthing",
											"Birth Photography",
											"Unmedicated Births",
											"Postpartum Chef",
											"Sleep consultant",
											"Childbirth Educator",
										]
											.filter(
												(option) =>
													!listing?.additional_specialties?.includes(option),
											)
											.map((option) => (
												<SelectItem key={option} value={option}>
													{option}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>

					{/* Row 2: Gender & Race */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Gender */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Gender
							</Label>
							<Select
								value={listing?.gender || ""}
								onValueChange={(value) => onListingChange("gender", value)}
							>
								<SelectTrigger className="mt-1 !bg-white !bg-opacity-100 backdrop-blur-none border border-newTheme-grey shadow-sm">
									<SelectValue placeholder="Select your gender..." />
								</SelectTrigger>
								<SelectContent className="bg-white border border-gray-200 shadow-lg">
									{["Female", "Male", "Non-Binary", "Transgender"].map(
										(option) => (
											<SelectItem key={option} value={option}>
												{option}
											</SelectItem>
										),
									)}
								</SelectContent>
							</Select>
						</div>

						{/* Race */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Race/Ethnicity
							</Label>
							<Select
								value={listing?.race || ""}
								onValueChange={(value) => onListingChange("race", value)}
							>
								<SelectTrigger className="mt-1 !bg-white !bg-opacity-100 backdrop-blur-none border border-newTheme-grey shadow-sm">
									<SelectValue placeholder="Select your race/ethnicity..." />
								</SelectTrigger>
								<SelectContent className="bg-white border border-gray-200 shadow-lg">
									{[
										"Caucasian",
										"Black",
										"Hispanic",
										"Native American",
										"Pacific Islander",
										"South Asian",
										"East Asian",
										"Prefer not to say",
									].map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					{/* Row 3: Religion & Languages Spoken */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Religion */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Religion
							</Label>
							<Select
								value={listing?.religion || ""}
								onValueChange={(value) => onListingChange("religion", value)}
							>
								<SelectTrigger className="mt-1 !bg-white !bg-opacity-100 backdrop-blur-none border border-newTheme-grey shadow-sm">
									<SelectValue placeholder="Select your religion..." />
								</SelectTrigger>
								<SelectContent className="bg-white border border-gray-200 shadow-lg">
									{[
										"Christian",
										"Muslim",
										"Hindu",
										"Jewish",
										"Buddhist",
										"Prefer not to say",
									].map((option) => (
										<SelectItem key={option} value={option}>
											{option}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Languages Spoken */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Languages Spoken
							</Label>
							<div className="mt-2 space-y-3">
								<div className="flex flex-wrap gap-2">
									{listing?.languages_spoken?.length ? (
										listing.languages_spoken.map((language, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-indigo-100 text-indigo-800 px-3 py-1 flex items-center gap-2"
											>
												{language}
												<button
													onClick={() => {
														const updatedLanguages =
															listing.languages_spoken?.filter(
																(_, i) => i !== index,
															) || [];
														onListingChange(
															"languages_spoken",
															updatedLanguages,
														);
													}}
													className="hover:text-red-600 transition-colors"
													type="button"
												>
													×
												</button>
											</Badge>
										))
									) : (
										<p className="text-gray-500 text-sm font-karla">
											No languages added yet
										</p>
									)}
								</div>

								<Select
									onValueChange={(value) => {
										if (value && !listing?.languages_spoken?.includes(value)) {
											const updatedLanguages = [
												...(listing?.languages_spoken || []),
												value,
											];
											onListingChange("languages_spoken", updatedLanguages);
										}
									}}
								>
									<SelectTrigger className="!bg-white !bg-opacity-100 backdrop-blur-none border border-newTheme-grey shadow-sm">
										<SelectValue placeholder="Add languages..." />
									</SelectTrigger>
									<SelectContent className="bg-white border border-gray-200 shadow-lg">
										{[
											"English",
											"Spanish",
											"Albanian",
											"American Sign Language",
											"Arabic",
											"Armenian",
											"Assamese",
											"Bengali",
											"Bosnian",
											"Bulgarian",
											"Cantonese",
											"Croatian",
											"Danish",
											"Dutch",
											"Farsi",
											"Flemish",
											"French",
											"Fuzhounese",
											"German",
											"Greek",
											"Gujarati",
											"Haitian Creole",
											"Hebrew",
											"Hindi",
											"Hmong",
											"Hungarian",
											"Icelandic",
											"Igbo",
											"Italian",
											"Japanese",
											"Kashmiri",
											"Korean",
											"Kurdish",
											"Malayalam",
											"Mandarin",
											"Marathi",
											"Norwegian",
											"Polish",
											"Portuguese",
											"Punjabi",
											"Romanian",
											"Russian",
											"Serbian",
											"Shona",
											"Somali",
											"Swahili",
											"Tagalog",
											"Thai",
											"Turkish",
											"Twi",
											"Ukrainian",
											"Urdu",
											"Vietnamese",
											"Yiddish",
											"Yoruba",
										]
											.filter(
												(option) =>
													!listing?.languages_spoken?.includes(option),
											)
											.map((option) => (
												<SelectItem key={option} value={option}>
													{option}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>

					{/* Row 4: Service Types & Doula Style */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						{/* Service Types */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Service Types
							</Label>
							<div className="mt-2 space-y-3">
								<div className="flex flex-wrap gap-2">
									{listing?.doula_service_type?.length ? (
										listing.doula_service_type.map((type, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-blue-100 text-blue-800 px-3 py-1 flex items-center gap-2"
											>
												{type}
												<button
													onClick={() => {
														const updatedTypes =
															listing.doula_service_type?.filter(
																(_, i) => i !== index,
															) || [];
														onListingChange("doula_service_type", updatedTypes);
													}}
													className="hover:text-red-600 transition-colors"
													type="button"
												>
													×
												</button>
											</Badge>
										))
									) : (
										<p className="text-gray-500 text-sm font-karla">
											No service types added yet
										</p>
									)}
								</div>

								<Select
									onValueChange={(value) => {
										if (
											value &&
											!listing?.doula_service_type?.includes(value)
										) {
											const updatedTypes = [
												...(listing?.doula_service_type || []),
												value,
											];
											onListingChange("doula_service_type", updatedTypes);
										}
									}}
								>
									<SelectTrigger className="!bg-white !bg-opacity-100 backdrop-blur-none border border-newTheme-grey shadow-sm">
										<SelectValue placeholder="Add service types..." />
									</SelectTrigger>
									<SelectContent className="bg-white border border-gray-200 shadow-lg">
										{[
											"Birth Doula",
											"Daytime Postpartum Doula",
											"Overnight Postpartum Doula",
										]
											.filter(
												(option) =>
													!listing?.doula_service_type?.includes(option),
											)
											.map((option) => (
												<SelectItem key={option} value={option}>
													{option}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>
						</div>

						{/* Doula Style */}
						<div>
							<Label className="text-sm font-medium text-newTheme-grey font-karla">
								Doula Style
							</Label>
							<div className="mt-2 space-y-3">
								<div className="flex flex-wrap gap-2">
									{listing?.style?.length ? (
										listing.style.map((styleItem, index) => (
											<Badge
												key={index}
												variant="secondary"
												className="bg-pink-100 text-pink-800 px-3 py-1 flex items-center gap-2"
											>
												{styleItem}
												<button
													onClick={() => {
														const updatedStyles =
															listing.style?.filter((_, i) => i !== index) ||
															[];
														onListingChange("style", updatedStyles);
													}}
													className="hover:text-red-600 transition-colors"
													type="button"
												>
													×
												</button>
											</Badge>
										))
									) : (
										<p className="text-gray-500 text-sm font-karla">
											No style traits added yet
										</p>
									)}
								</div>

								<Select
									onValueChange={(value) => {
										if (value && !listing?.style?.includes(value)) {
											const updatedStyles = [...(listing?.style || []), value];
											onListingChange("style", updatedStyles);
										}
									}}
								>
									<SelectTrigger className="!bg-white !bg-opacity-100 backdrop-blur-none border border-newTheme-grey shadow-sm">
										<SelectValue placeholder="Add style traits..." />
									</SelectTrigger>
									<SelectContent className="bg-white border border-gray-200 shadow-lg">
										{[
											"practical",
											"spiritual",
											"mindful",
											"warm",
											"assertive",
											"humorous",
											"holistic",
											"open-minded",
											"direct",
											"earthy",
											"affirming",
											"empowering",
											"motivational",
											"compassionate",
											"creative",
											"analytical",
											"energetic",
											"encouraging",
										]
											.filter((option) => !listing?.style?.includes(option))
											.map((option) => (
												<SelectItem key={option} value={option}>
													{option}
												</SelectItem>
											))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	);
}
